import _, {first} from 'lodash';
import {createSelector} from 'reselect';

import {isWriteAccessCalendarVersion} from '../../../helpers/models/calendarVersionHelpers';
import {channelSupportsCalendarSync} from '../../../helpers/models/channelHelpers';
import {AppParametricSelector} from '../../../helpers/reselectHelpers';
import {CalendarTeammateAccessRolesEnum} from '../../../models/api/calendarModel';
import {CalendarVersionAccessRolesEnum, CalendarVersionModel} from '../../../models/api/calendarVersionModel';
import {DefaultTeammateAccessRolesEnum} from '../../../models/api/companyPreferencesModel';
import {getChannels} from '../channels/channelsSelectors';
import {getDefaultTeammateAccessRole} from '../company/companySelectors';
import {getCalendars, getCalendarVersions} from '../dataSelectors';
import {getDefaultCalendarVersionId} from '../preferences/preferencesSelectors';

export const getFlatCalendarVersions = createSelector(
  [getCalendarVersions, getChannels],
  (calendarVersions, channels) =>
    _(calendarVersions)
      .values()
      .compact()
      .filter((v) => {
        // Always include find-a-time calendarVersions.
        if (!v.channelId) {
          return true;
        }

        const channel = channels[v.channelId];
        if (!channel) {
          return false;
        }

        return channelSupportsCalendarSync(channel);
      })
      .value(),
);

export const getVisibleCalendarVersions = createSelector(
  [getCalendars, getFlatCalendarVersions, getDefaultTeammateAccessRole],
  (calendars, calendarVersions, defaultTeammateAccessRole) =>
    calendarVersions.filter((calendarVersion) => {
      if (calendarVersion.isHidden) {
        return false;
      }

      const calendar = calendars[calendarVersion.calendarId];
      if (!calendar) {
        return false;
      }

      // Hide calendarVersions that have no access -- the calendarVersion inherits its accessRole and one of the following is true:
      // - The associated Calendar provides no teammate access
      // - The associated Calendar inherits its teammateAccessRole from the company default, and the company default is no access.
      const hasInheritedAccessRole = calendarVersion.accessRole === CalendarVersionAccessRolesEnum.INHERIT;
      const hasNoInheritedAccess =
        calendar.teammateAccessRole === CalendarTeammateAccessRolesEnum.NONE ||
        (calendar.teammateAccessRole === CalendarTeammateAccessRolesEnum.INHERIT &&
          defaultTeammateAccessRole === DefaultTeammateAccessRolesEnum.NONE);

      return !(hasInheritedAccessRole && hasNoInheritedAccess);
    }),
);

export const getHasNoCalendars = createSelector(
  getVisibleCalendarVersions,
  (calendarVersions) => calendarVersions.length === 0,
);

export const getPrimaryCalendarVersions = createSelector(
  getVisibleCalendarVersions,
  (visibleCalendarVersions) => visibleCalendarVersions.filter((v) => v.isPrimary),
);

export const getSelectedCalendarVersions = createSelector(
  getVisibleCalendarVersions,
  (visibleCalendarVersions) => visibleCalendarVersions.filter((v) => v.isSelected),
);

export const getWriteAccessCalendarVersions = createSelector(
  getVisibleCalendarVersions,
  (visibleCalendarVersions) => visibleCalendarVersions.filter((v) => isWriteAccessCalendarVersion(v)),
);

export const getWriteAccessCalendarVersionIds = createSelector(
  getWriteAccessCalendarVersions,
  (calendarVersions) => calendarVersions.map((calendarVersion) => calendarVersion.id),
);

export const getWriteAccessCalendarData = createSelector(
  [getWriteAccessCalendarVersions, getCalendars],
  (writeAccessCalendarVersions, calendars) =>
    _(writeAccessCalendarVersions)
      .map((calendarVersion) => {
        const calendar = calendars[calendarVersion.calendarId];
        return !calendar ? null : {calendar, calendarVersion};
      })
      .compact()
      .value(),
);

export const getDefaultCalendarVersion = createSelector(
  [getVisibleCalendarVersions, getDefaultCalendarVersionId],
  (visibleCalendarVersions, defaultCalendarVersionId) => {
    if (!defaultCalendarVersionId) {
      return undefined;
    }

    return visibleCalendarVersions.find((cv) => cv.id === defaultCalendarVersionId);
  },
);

export const getComposeCalendarVersion = createSelector(
  [getDefaultCalendarVersion, getPrimaryCalendarVersions],
  (defaultCalendarVersion, primaryCalendarVersions) =>
    defaultCalendarVersion || first(primaryCalendarVersions),
);

type GetCalendarVersionId = AppParametricSelector<any, number>;
export function makeGetCalendarData(getCalendarVersionId: GetCalendarVersionId) {
  return createSelector(
    [getCalendarVersionId, getCalendarVersions, getCalendars],
    (calendarVersionId, calendarVersions, calendars) => {
      const calendarVersion = calendarVersions[calendarVersionId];
      if (!calendarVersion) {
        throw new Error('Unable to find calendar version');
      }

      const calendar = calendars[calendarVersion.calendarId];
      if (!calendar) {
        throw new Error('Unable to find calendar');
      }

      return {calendar, calendarVersion};
    },
  );
}

export const getFindCalendarVersionName = createSelector(
  getCalendars,
  (calendars) => (calendarVersion: CalendarVersionModel) => {
    if (calendarVersion.nameOverride) {
      return calendarVersion.nameOverride;
    }

    const calendar = calendars[calendarVersion.calendarId];
    if (calendar) {
      return calendar.name;
    }

    return '';
  },
);
